import axios, {
  type CreateAxiosDefaults,
  type Axios,
  type AxiosError,
  type AxiosRequestConfig,
} from 'axios';

export class GenericRestClient<TOp extends string, TInput extends object, TResult extends object> {
  protected client: Axios;

  constructor(clientDefaults: CreateAxiosDefaults = {}) {
    this.client = axios.create(clientDefaults);
  }

  protected streamHeaders(): HeadersInit | Promise<HeadersInit> {
    return {};
  }

  protected async stream(path: string, signal: AbortSignal): Promise<Response> {
    const headers = await this.streamHeaders();

    return fetch(`${this.client.defaults.baseURL}/${path}`, { headers, signal });
  }

  protected async request<TReq, TRes>(
    operation: TOp,
    params: TReq,
    methodKey: 'post' | 'get' | 'delete' | 'put' = 'post',
    config?: AxiosRequestConfig
  ) {
    const responsePromise =
      methodKey === 'get' || methodKey === 'delete'
        ? this.client[methodKey]<TRes>(operation, { ...config, params })
        : this.client[methodKey]<TRes>(operation, params, config);

    return responsePromise
      .then((res) => res.data)
      .catch((e: AxiosError) => {
        throw new Error(e.message || 'Unknown error');
      });
  }

  protected createRequest<Op extends TOp & keyof TInput & keyof TResult>(
    operation: Op,
    methodKey: 'post' | 'get' | 'delete' | 'put' = 'post',
    axiosConfig?: AxiosRequestConfig
  ) {
    return (params: TInput[Op], additionalAxiosConfig?: AxiosRequestConfig) =>
      this.request<TInput[Op], TResult[Op]>(operation, params, methodKey, {
        ...axiosConfig,
        ...additionalAxiosConfig,
      });
  }
}
